import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CTableWrapper, DIdAutocomplete, ICustomTableWrapperProps, SortedOrder } from '../../../../dum';
import { useProductionPerformanceProductMachineSummaryLazyQuery } from '../../../../../services';
import { formatDate, formatFloat } from '../../../../../helpers';
import { ProductionPerformanceAnalysis } from '../production-performance-analysis';
import { usePerfFilters } from '../perf-filters-context';
import { SHIFT_HOURS } from '../../../../smart/production-performance';

const MIN_RUNS_FOR_INDEX = 3;

interface MachineRow {
    machineId: number;
    machineName: string;
    runs: number;
    kilos: number;
    hours: number;
    kgHr: number | null;
    // Bultos/hr and its 8 h-shift projection — the unit the floor plans in.
    bultosHr: number | null;
    bultosShift: number | null;
    // Product-wide kg/hr (totals-over-totals across all returned rows) used as
    // the índice denominator — computed after all rows are available.
    index: number | null;
    mermaPct: number;
    lastRunDate: string | null;
}

interface IProductsTabProps {
    machines: { id: number; name: string; categoryName: string }[];
    products: { id: number; description: string; categoryName: string }[];
}

export function ProductsTab({ machines, products }: IProductsTabProps) {
    const { fromDate, toDate, singleProductOnly } = usePerfFilters();
    const [productId, setProductId] = useState<number | null>(null);
    // Optional machine sub-filter (set by the autocomplete or a machine-row click).
    const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const [querySummary, { data, loading }] =
        useProductionPerformanceProductMachineSummaryLazyQuery();

    // Refetch on product or window change. The machine sub-filter is reset only
    // when the PRODUCT changes (in the autocomplete's onChange), never here — so
    // moving Desde/Hasta keeps the selected machine instead of clearing it.
    useEffect(() => {
        if (productId) {
            void querySummary({
                variables: {
                    productId,
                    fromDate: fromDate || null,
                    toDate: toDate || null,
                },
            });
        }
    }, [productId, fromDate, toDate, querySummary]);

    // Machine sub-filter options — reuse the overview rows already fetched.
    const machineOptions = useMemo(() => {
        const categoryNames = new Map(
            machines.map((machine) => [machine.id, machine.categoryName]),
        );
        return (data?.getProductMachinePerformanceSummary ?? [])
            .map((r) => ({
                id: r.machine_id,
                name: r.machine_name,
                categoryName:
                    categoryNames.get(r.machine_id) ??
                    'Sin tipo de producción',
            }))
            .sort((a, b) =>
                a.categoryName.localeCompare(b.categoryName, 'es', {
                    sensitivity: 'base',
                }) ||
                a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
            );
    }, [data, machines]);

    const { rows, totalsRow } = useMemo(() => {
        const raw = data?.getProductMachinePerformanceSummary ?? [];

        // Product-wide totals-over-totals kg/hr — the índice denominator.
        const totalKilos = raw.reduce((s, r) => s + r.kilos, 0);
        const totalHours = raw.reduce((s, r) => s + r.hours, 0);
        const totalGroups = raw.reduce((s, r) => s + r.groups, 0);
        const productKgHr = totalHours > 0 ? totalKilos / totalHours : null;
        const productBultosHr = totalHours > 0 ? totalGroups / totalHours : null;
        const productBultosShift =
            productBultosHr === null ? null : productBultosHr * SHIFT_HOURS;

        const machineRows: MachineRow[] = raw.map((r) => {
            const kgHr = r.hours > 0 ? r.kilos / r.hours : null;
            const bultosHr = r.hours > 0 ? r.groups / r.hours : null;
            let index: number | null = null;
            if (kgHr !== null && productKgHr !== null && r.runs >= MIN_RUNS_FOR_INDEX) {
                index = (kgHr / productKgHr) * 100;
            }
            return {
                machineId: r.machine_id,
                machineName: r.machine_name,
                runs: r.runs,
                kilos: r.kilos,
                hours: r.hours,
                kgHr,
                bultosHr,
                bultosShift: bultosHr === null ? null : bultosHr * SHIFT_HOURS,
                index,
                mermaPct: r.kilos > 0 ? (r.waste_share_total / r.kilos) * 100 : 0,
                lastRunDate: r.last_run_date
                    ? formatDate(r.last_run_date)
                    : null,
            };
        });

        const totalRuns = raw.reduce((s, r) => s + r.runs, 0);
        const totalWaste = raw.reduce((s, r) => s + r.waste_share_total, 0);
        const totals = [
            'Total',
            formatFloat(totalRuns),
            formatFloat(totalKilos),
            formatFloat(totalHours),
            productKgHr !== null ? formatFloat(productKgHr) : '—',
            productBultosHr !== null ? formatFloat(productBultosHr) : '—',
            productBultosShift !== null ? formatFloat(productBultosShift) : '—',
            '100',
            totalKilos > 0 ? `${formatFloat((totalWaste / totalKilos) * 100)}%` : '—',
            null,
        ];

        // Column index → sort key (must match tableItems order below).
        // 0=Máquina, 1=Corridas, 2=Kilos, 3=Horas, 4=kg/hr, 5=Bultos/hr,
        // 6=Turno(8h), 7=Índice, 8=Merma%, 9=Última corrida
        if (sortedIndex !== null && sortedOrder !== null) {
            machineRows.sort((a, b) => {
                let av: number | string | null;
                let bv: number | string | null;
                switch (sortedIndex) {
                    case 0: av = a.machineName; bv = b.machineName; break;
                    case 1: av = a.runs; bv = b.runs; break;
                    case 2: av = a.kilos; bv = b.kilos; break;
                    case 3: av = a.hours; bv = b.hours; break;
                    case 4: av = a.kgHr; bv = b.kgHr; break;
                    case 5: case 6: av = a.bultosHr; bv = b.bultosHr; break;
                    case 7: av = a.index; bv = b.index; break;
                    case 8: av = a.mermaPct; bv = b.mermaPct; break;
                    case 9: av = a.lastRunDate; bv = b.lastRunDate; break;
                    default: return 0;
                }
                if (av === null && bv === null) return 0;
                if (av === null) return 1;
                if (bv === null) return -1;
                const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number);
                return sortedOrder === 'asc' ? cmp : -cmp;
            });
        }

        return { rows: machineRows, totalsRow: totals };
    }, [data, sortedIndex, sortedOrder]);

    const tableItems = useMemo<ICustomTableWrapperProps<MachineRow>['items']>(
        () => [
            {
                title: 'Máquina',
                textAlign: 'left',
                cellGrow: 2,
                sortable: true,
                content: (r) => r.machineName,
            },
            {
                title: 'Corridas',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.runs),
            },
            {
                title: 'Kilos',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.kilos),
            },
            {
                title: 'Horas',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.hours),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.kgHr === null ? '—' : formatFloat(r.kgHr),
            },
            {
                title: 'Bultos/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.bultosHr === null ? '—' : formatFloat(r.bultosHr),
            },
            {
                title: 'Turno (8 h)',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.bultosShift === null ? '—' : formatFloat(r.bultosShift),
            },
            {
                title: 'Índice vs promedio',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.index === null ? (
                        <Typography
                            variant={'body2'}
                            sx={{ color: 'text.disabled' }}
                        >
                            {r.runs < MIN_RUNS_FOR_INDEX ? `< ${MIN_RUNS_FOR_INDEX} corridas` : '—'}
                        </Typography>
                    ) : (
                        `${formatFloat(r.index)}`
                    ),
            },
            {
                title: 'Merma %',
                textAlign: 'right',
                sortable: true,
                content: (r) => `${formatFloat(r.mermaPct)}%`,
            },
            {
                title: 'Última corrida',
                textAlign: 'left',
                sortable: true,
                content: (r) => r.lastRunDate ?? '—',
            },
        ],
        [],
    );

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ minWidth: 280 }}>
                    <DIdAutocomplete
                        id={productId}
                        onChange={(id) => {
                            setProductId(id);
                            setSelectedMachineId(null);
                        }}
                        itemText={'description'}
                        label={'Producto'}
                        options={products}
                        groupBy={(product) => product.categoryName}
                    />
                </Box>
                {productId ? (
                    <Box sx={{ minWidth: 240 }}>
                        <DIdAutocomplete
                            id={selectedMachineId}
                            onChange={setSelectedMachineId}
                            itemText={'name'}
                            label={'Máquina (opcional)'}
                            options={machineOptions}
                            groupBy={(machine) => machine.categoryName}
                        />
                    </Box>
                ) : null}
            </Box>

            {!productId ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant={'body1'} sx={{ color: 'text.secondary' }}>
                        Selecciona un producto para comparar su rendimiento por máquina.
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* flexShrink: 0 — see machines-tab: TableContainer's overflow
                        makes it collapse to a sliver inside the tab's flex column
                        without this. */}
                    <Box sx={{ flexShrink: 0 }}>
                        <CTableWrapper
                            items={tableItems}
                            data={rows}
                            totalsRow={totalsRow}
                            loading={loading}
                            sortedIndex={sortedIndex}
                            setSortedIndex={setSortedIndex}
                            sortedOrder={sortedOrder}
                            setSortedOrder={setSortedOrder}
                            onRowDoubleClick={(r) =>
                                setSelectedMachineId(
                                    r.machineId === selectedMachineId
                                        ? null
                                        : r.machineId,
                                )
                            }
                            rowSx={(r) =>
                                r.machineId === selectedMachineId
                                    ? { backgroundColor: 'action.selected' }
                                    : {}
                            }
                        />
                    </Box>

                    <Box sx={{ mt: 3, flexShrink: 0 }}>
                        <ProductionPerformanceAnalysis
                            productId={productId}
                            machineId={selectedMachineId}
                            fromDate={fromDate}
                            toDate={toDate}
                            singleProductOnly={singleProductOnly}
                        />
                    </Box>
                </>
            )}
        </>
    );
}
